import * as pdfjsLib from "pdfjs-dist";
import jsPDF from "jspdf";
import { MAX_PREVIEW_SIDE } from "components/SmartSurveyor/ReviewPage/FloorPlanPanelPdf/constants";
import {
  composePdfViewportRotation,
  createDisplayTransform,
  getRasterRotationOffset,
  getRotatedSize,
} from "components/SmartSurveyor/ReviewPage/FloorPlanPanelWrapper/createDisplayTransform";
import {
  scaleCircuitPaths,
  scaleDetectedObjects,
  scalePolygonsOverlay,
} from "components/SmartSurveyor/ReviewPage/FloorPlanPanelWrapper/utils";
import { loadImage } from "utils/loadImage";
import { renderOverlaySvgString } from "./renderOverlaySvgString";
import { rasterizeSvgOntoCanvas } from "./rasterizeSvgOntoCanvas";
import { drawLegendOntoCanvas } from "./drawLegendOntoCanvas";
import { drawMoncoBrandLink } from "./drawMoncoBrandLink";
import { appendRoomSectionsPages } from "./appendRoomSectionsPages";
import { captureNoteCrops } from "./captureNoteCrops";
import { appendNotePages } from "./appendNotePages";
import type { ExportFloorPlanToPdf } from "./types";

pdfjsLib.GlobalWorkerOptions.workerSrc = `/pdf.worker.min.mjs`;

export const exportFloorPlanToPdf: ExportFloorPlanToPdf = async ({
  pdfUrl,
  pageNumber,
  fileName,
  detectedObjects,
  selectedObject,
  circuitPaths,
  polygonsOverlay,
  imageUrl,
  legend,
  roomSections,
  noteAppendix,
  rotation,
}) => {
  const loadingTask = pdfjsLib.getDocument({ url: pdfUrl });
  const pdfDocument = await loadingTask.promise;

  try {
    const page = await pdfDocument.getPage(pageNumber);

    const baseViewport = page.getViewport({ scale: 1 });
    const unrotatedPageWidth = baseViewport.width;
    const unrotatedPageHeight = baseViewport.height;
    const { width: pdfPageWidth, height: pdfPageHeight } = getRotatedSize({
      rotation,
      width: unrotatedPageWidth,
      height: unrotatedPageHeight,
    });

    // The same transform the viewer uses, at scale 1: the overlay is authored in PDF points.
    const transform = createDisplayTransform({
      rotation,
      scaleFactor: 1,
      unrotatedDisplayWidth: unrotatedPageWidth,
      unrotatedDisplayHeight: unrotatedPageHeight,
    });
    const rotatedDetectedObjects = scaleDetectedObjects({
      objects: detectedObjects,
      transform,
    });
    const rotatedSelectedObject = selectedObject
      ? scaleDetectedObjects({ objects: [selectedObject], transform })[0]
      : undefined;
    const rotatedCircuitPaths = circuitPaths
      ? scaleCircuitPaths({ state: circuitPaths, transform })
      : undefined;
    const rotatedPolygonsOverlay = polygonsOverlay
      ? scalePolygonsOverlay({ overlay: polygonsOverlay, transform })
      : undefined;
    const noteEntries =
      noteAppendix === undefined
        ? []
        : noteAppendix.entries.map((entry) => ({
            ...entry,
            bbox: transform.toDisplayBbox(entry.bbox),
          }));

    const canvas = document.createElement("canvas");
    const context = canvas.getContext("2d");

    if (!context) {
      throw new Error("Failed to get 2D context for export canvas");
    }

    if (imageUrl) {
      const backgroundImage = await loadImage(imageUrl);
      const rotatedImageSize = getRotatedSize({
        rotation,
        width: backgroundImage.naturalWidth,
        height: backgroundImage.naturalHeight,
      });
      // TODO: cap this like the pdf.js branch; an 8192 px plan image makes every later step pay for 47 megapixels.
      canvas.width = rotatedImageSize.width;
      canvas.height = rotatedImageSize.height;
      const { translateX, translateY } = getRasterRotationOffset({
        rotation,
        width: backgroundImage.naturalWidth,
        height: backgroundImage.naturalHeight,
      });
      context.translate(translateX, translateY);
      context.rotate((rotation * Math.PI) / 180);
      context.drawImage(backgroundImage, 0, 0);
      context.setTransform(1, 0, 0, 1, 0, 0);
    } else {
      const renderScale = Math.min(
        MAX_PREVIEW_SIDE / pdfPageWidth,
        MAX_PREVIEW_SIDE / pdfPageHeight,
      );
      const renderViewport = page.getViewport({
        scale: renderScale,
        rotation: composePdfViewportRotation({
          pageRotate: page.rotate,
          rotation,
        }),
      });
      canvas.width = Math.round(renderViewport.width);
      canvas.height = Math.round(renderViewport.height);
      await page.render({
        canvasContext: context,
        canvas,
        viewport: renderViewport,
      }).promise;
    }

    // Taken before the overlay so the crops show the clean plan, not the inflated marker boxes.
    const noteCrops =
      noteEntries.length > 0
        ? captureNoteCrops({
            sourceCanvas: canvas,
            entries: noteEntries,
            pdfPageWidth,
            pdfPageHeight,
          })
        : undefined;

    const overlaySvgString = renderOverlaySvgString({
      pdfPageWidth,
      pdfPageHeight,
      pixelWidth: canvas.width,
      pixelHeight: canvas.height,
      detectedObjects: rotatedDetectedObjects,
      selectedObject: rotatedSelectedObject,
      circuitPaths: rotatedCircuitPaths,
      polygonsOverlay: rotatedPolygonsOverlay,
    });

    await rasterizeSvgOntoCanvas({
      svgString: overlaySvgString,
      context,
      canvasWidth: canvas.width,
      canvasHeight: canvas.height,
    });

    if (legend) {
      await drawLegendOntoCanvas({
        legend,
        context,
        canvasWidth: canvas.width,
        canvasHeight: canvas.height,
        pdfPageWidth,
        pdfPageHeight,
        detectedObjects: rotatedDetectedObjects,
        circuitPaths: rotatedCircuitPaths,
      });
    }

    // TODO: encode with canvas.toBlob and give addImage the bytes; toDataURL plus the base64 decode inside addImage block the UI for seconds on large plans.
    const dataUrl = canvas.toDataURL("image/jpeg", 1.0);

    const orientation =
      pdfPageWidth >= pdfPageHeight ? "landscape" : "portrait";

    const document_ = new jsPDF({
      unit: "pt",
      format: [pdfPageWidth, pdfPageHeight],
      orientation,
    });

    document_.addImage(dataUrl, "JPEG", 0, 0, pdfPageWidth, pdfPageHeight);

    await drawMoncoBrandLink({
      doc: document_,
      pdfPageWidth,
      pdfPageHeight,
    });

    if (roomSections) {
      await appendRoomSectionsPages({
        doc: document_,
        roomSections,
        pdfPageWidth,
        pdfPageHeight,
      });
    }

    if (noteAppendix !== undefined && noteCrops !== undefined) {
      await appendNotePages({
        doc: document_,
        noteAppendix,
        entries: noteEntries,
        crops: noteCrops,
        pdfPageWidth,
        pdfPageHeight,
      });
    }

    document_.save(fileName);
  } finally {
    await pdfDocument.destroy();
  }
};
